import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, Optional } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { JwtHelperService } from '@auth0/angular-jwt';
import { TranslateService } from '@ngx-translate/core';
import { CommunicationChannelEnum, IGeo, IVisitorsAreaV2, SectionCTAEnum } from '@smarttypes/core';
import { IApartmentInfo, IGuest, IReservation, IRoom } from '@smarttypes/hotel';
import { WindowService } from 'angular-v2-services';
import { GUEST } from 'angular-v2-utils';
import { get, isObject, set } from 'lodash';
import { BehaviorSubject, catchError, filter, Observable, of, shareReplay, tap, throwError } from 'rxjs';

import { environment } from '../../environments/environment';

@Injectable({
  providedIn: 'root',
})
export class VisitorsAreaV2Service {
  private _config!: IVisitorsAreaV2;
  private _guest!: IGuest;
  private configRequest$?: Observable<IVisitorsAreaV2>;
  private guest$: BehaviorSubject<IGuest | null> = new BehaviorSubject<IGuest | null>(null);

  constructor(
    private readonly httpClient: HttpClient,
    private windowService: WindowService,
    private readonly domSanitizer: DomSanitizer,
    private readonly router: Router,
    private readonly translateService: TranslateService,
    @Optional() private readonly jwtService: JwtHelperService,
  ) {}

  get guestId(): string | null {
    return localStorage.getItem('guestId') as string | null;
  }

  get token(): string | null {
    const token = localStorage.getItem('sh-k-t') as string | null;
    if (token && !this.jwtService?.isTokenExpired(token)) {
      return token;
    }
    localStorage.removeItem('sh-k-t');
    return null;
  }

  set token(token: string | null) {
    if (!token || this.jwtService?.isTokenExpired(token)) {
      localStorage.removeItem('sh-k-t');
      return;
    }
    localStorage.setItem('sh-k-t', token);
    return;
  }

  get linkedCompanies() {
    return this.httpClient.get<{
      companies: { name: string; companyId: string; guestId: string }[];
    }>(`/visitors-area-v2/linked-companies/${this.guestId}`);
  }

  $guestChange(): Observable<IGuest | null> {
    return this.guest$.asObservable().pipe(
      filter(g => !!g),
      tap(g => {
        localStorage.setItem('guestId', g?._id as string);
      }),
    );
  }

  initDemoGuest(room?: IRoom): IGuest {
    const guest: IGuest = {
      ...GUEST,
      reservation: {
        ...GUEST.reservation,
        room: room,
      },
    };
    this.guest$.next(guest);
    return guest;
  }

  login(
    email?: string,
    phone?: string,
    companyId?: string,
  ): Observable<{
    requestId: string;
    guests?: { name: string; companyId: string }[];
  }> {
    return this.httpClient.post<any>(`/visitors-area-v2/login`, {
      email,
      phone,
      companyId,
    });
  }

  sendCode(token: string, code: string, email?: string, phone?: string, companyId?: string): Observable<any> {
    return this.httpClient.post<{ id: string }>(`/visitors-area-v2/login`, {
      email,
      token,
      code,
      phone,
      companyId,
    });
  }

  apartmentByGuest(guestId: string): Observable<any> {
    return this.httpClient.get<any>(`/visitors-area-v2/public/apartment/by-guest/${guestId}`);
  }

  callToAction(
    guestId: string,
    companyId: string,
    channel: CommunicationChannelEnum,
    section: SectionCTAEnum,
    request?: string,
  ): Observable<void> {
    let params = new HttpParams()
      .set('id', guestId)
      .set('companyId', companyId)
      .set('channel', channel);
    if (request) {
      params = params.set('request', request);
    }
    return this.httpClient.get<void>(`/visitors-area-v2/public/cta/${section}`, { params });
  }

  getConfig(): Observable<IVisitorsAreaV2> {
    if (!this.configRequest$) {
      this.configRequest$ = this.httpClient
        .get<IVisitorsAreaV2>(`${environment.api}/visitors-area-v2/public?domain=${this.getDomain()}`)
        .pipe(
          catchError(err => {
            this.router.navigate(['404']);
            return throwError(() => err);
          }),
          tap(config => (this._config = config)),
          shareReplay({ bufferSize: 1, refCount: false }),
        );
    }
    return this.configRequest$;
  }

  getGuest(id?: string, reload = false): Observable<IGuest> {
    if (id && !id.match(/^[0-9a-fA-F]{24}$/)) {
      id = undefined;
    }

    if (id) {
      localStorage.setItem('guestId', id as string);
    }

    if (!id && localStorage.getItem('guestId')) {
      id = localStorage.getItem('guestId') as string;
    }

    if ((this._guest || !id) && !reload) {
      return of(this._guest);
    } else {
      return this.httpClient.get<IGuest>(`/visitors-area-v2/public/visitor/${id}`).pipe(
        catchError(err => {
          localStorage.removeItem('guestId');
          this.router.navigate(['no-reservation']);
          return throwError(() => err);
        }),
        tap(guest => {
          if (get(guest, 'config.shouldRedirect')) {
            window.location.assign(`https://${get(guest, 'config.shouldRedirect')}.mysmartguest.com/${guest._id}`);
          }

          this._guest = guest;
          this.guest$.next(guest);

          const langs = ((this._guest?.reservation?.room as IRoom)?.config as any)?.languages ?? [];
          if (langs.length > 0 && this._config) {
            set(
              this._config,
              'languages',
              langs.map((l: string) => (l === 'ua' ? 'uk' : l)),
            );
          }

          if (guest._id) {
            localStorage.setItem('guestId', id as string);
          }

          if (!this._guest?.reservation?.actions?.guestAreaVisitedAt) {
            this.visit(guest._id as string);
          }
        }),
      );
    }
  }

  getLanguages(): string[] {
    return this._config.languages ?? [];
  }

  getDomain(): string {
    if (
      this.windowService.location.host.includes('localhost') ||
      this.windowService.location.host.includes('web.app')
    ) {
      return environment?.demoGA ?? '';
    } else {
      const host = this.windowService.location.host;
      if (host.includes('m.guestbook.smartguest.co')) {
        return 'guestbook.smartguest.co';
      }
      return host;
    }
  }

  replaceSecrets(text: string, apartmentInfo?: IApartmentInfo) {
    const pathsMap = new Map([
      ['floorNo', 'floor'],
      ['apartmentNo', 'address.apartmentNo'],
      ['stairwellCode', 'accessSecret.codes.stairwellCode'],
      ['doorCode', 'accessSecret.codes.doorCode'],
      ['gateCode', 'accessSecret.codes.gateCode'],
      ['country', 'address.country'],
      ['city', 'address.city'],
      ['street', 'address.street'],
      ['postalCode', 'address.postalCode'],
      ['wifiName', 'wifi.ssid'],
      ['wifiPassword', 'wifi.password'],
      ['googleMapsPin', 'address.geo'],
      ['checkInFromTime', 'checkInFrom'],
      ['checkOutUntilTime', 'checkOutUntil'],
      ['checkInToTime', 'checkInTo'],
      ['propertyName', 'additionalInfo.propertyName'],
      ['wwwEN', 'additionalInfo.website.en'],
      ['wwwPL', 'additionalInfo.website.pl'],
    ]);

    text = text.replace(/\n/g, '<br />');

    pathsMap.forEach((path, field) => {
      const regex = new RegExp(`\\[${field}\\]`, 'g');
      let value = get(apartmentInfo, path, '');
      if (field === 'googleMapsPin' && isObject(value)) {
        value = this.generateGoogleMapsLink(value as IGeo);
      }
      text = text.replace(regex, `<strong>${value}</strong>`);
    });

    return text;
  }

  reservationContext(reservation?: IReservation, guestId?: string, companyId?: string, threadId?: string) {
    return this.httpClient.post<any>(`/visitors-area-v2/public/reservation-context`, {
      reservation: {
        from: reservation?.from,
        to: reservation?.to,
        reservationId: reservation?.reservationId,
      },
      guestId,
      companyId,
      threadId,
    });
  }

  accessLinkReminder(companyId: string, name: string, phone: string): Observable<void> {
    return this.httpClient.post<void>(`/visitors-area-v2/public/access-link-reminder`, {
      companyId,
      name,
      phone,
    });
  }

  descriptionByLang(room: IRoom, lang: string, path: string): string | SafeHtml {
    if (!room) {
      return '';
    }
    const obj: Record<string, string> = get(room, `${path}`, {});
    if (obj) {
      const info = obj[lang] || obj['en'] || '';
      const text = this.replaceSecrets(info ?? '', room.apartmentInfo);
      return this.domSanitizer.bypassSecurityTrustHtml(text);
    }
    return '';
  }

  visit(id: string) {
    return this.httpClient.get(`/visitors-area/public/visit/${id}`).subscribe();
  }

  apartmentLeft(id: string) {
    return this.httpClient.post(`/visitors-area-v2/public/apartment-left/${id}`, {
      leftApartment: true,
    });
  }

  amInApartment(id: string) {
    return this.httpClient.post(`/visitors-area-v2/public/entered-apartment/${id}`, {
      enteredApartment: true,
    });
  }

  private generateGoogleMapsLink(location: IGeo): string {
    const label = this.translateService.instant('GA.LINK_TO_GOOGLE_MAPS');
    return `<a href="https://www.google.com/maps/search/?api=1&query=${location.lat},${location.lng}" target="_blank">${label}</a>`;
  }
}
